import Icon1 from "@assets/icons/Icon";
import { UserAvatar } from "@components/ui/UserAvatar";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import DropDown from "@components/ui/dropdown/DropDown";
import { role_dict } from "@screens/sampleData";
import Button from "@components/ui/buttons/Button";
import { useState, useEffect, useRef } from "react";
import TextField from "@components/ui/textfields/TextField";
import { MemberItem } from "@components/ui/blocks/MemberAssignmentBlock";
import { User } from "types/user";
import { useDataStore } from "@store/user_org";
import { AccessType, isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";
import { usersApi } from "@services/api/users";
import Avvvatars from "avvvatars-react";
import { invitationsApi } from "@services/api/invitations";
import ConfirmationModal from "@components/support/ConfirmationModal";

const SEED_COUNT = 60;
function buildSeeds(base: string): string[] {
  return [base, ...Array.from({ length: SEED_COUNT - 1 }, (_, i) => `${base}-${i + 2}`)];
}

export const MemberModal = ({
  onClose,
  memberId,
}: {
  onClose: () => void;
  memberId: string;
}) => {
  const orgMembers = useDataStore((state) => state.orgMembers);
  const userData = useDataStore((state) => state.userData);
  const updateUserData = useDataStore((state) => state.updateUserData);
  const fetchOrgMembers = useDataStore((state) => state.fetchOrgMembers);
  const fetchUserData = useDataStore((state) => state.fetchUserData);

  const originalMember = orgMembers.find((u) => u.id === memberId);

  const [memberData, setMemberData] = useState<User | null>(
    originalMember || null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showPhotoSelector, setShowPhotoSelector] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isResendingInvitation, setIsResendingInvitation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Color palette for user tags
  const colorPalette = [
    "#EF4444", // red
    "#F97316", // orange
    "#F59E0B", // amber
    "#EAB308", // yellow
    "#84CC16", // lime
    "#22C55E", // green
    "#10B981", // emerald
    "#14B8A6", // teal
    "#06B6D4", // cyan
    "#0EA5E9", // sky
    "#3B82F6", // blue
    "#6366F1", // indigo
    "#8B5CF6", // violet
    "#A855F7", // purple
    "#D946EF", // fuchsia
    "#EC4899", // pink
    "#F43F5E", // rose
    "#64748B", // slate
  ];

  // Update memberData when orgMembers change
  useEffect(() => {
    const updatedMember = orgMembers.find((u) => u.id === memberId);
    if (updatedMember) {
      setMemberData(updatedMember);
    }
  }, [orgMembers, memberId]);

  const handleSave = async () => {
    if (!memberData) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      await updateUserData(memberData.id, {
        infos: memberData.infos,
        config: memberData.config,
        role: memberData.role,
        customization: memberData.customization,
      });

      // Refresh org members
      if (userData?.org_id) {
        await fetchOrgMembers(userData.org_id);
      }

      // Refetch user data if updating own profile
      if (memberData.id === userData?.id) {
        await fetchUserData(userData.id);
      }

      showNotification({
        type: "success",
        message: "Modifications enregistrées avec succès",
      });
      onClose();
    } catch (error: any) {
      console.error("Failed to update user:", error);
      const errorMessage = "Échec de l'enregistrement des modifications";
      setSaveError(errorMessage);
      showNotification({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !memberData) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      showNotification({
        type: "error",
        message: "Type de fichier invalide. Formats acceptés : JPG, PNG, WEBP",
      });
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      showNotification({
        type: "error",
        message: "La taille du fichier dépasse 5MB",
      });
      return;
    }

    setIsUploading(true);

    try {
      const result = await usersApi.uploadProfilePicture(memberData.id, file);

      // Update local state with new photo URL
      setMemberData({
        ...memberData,
        customization: {
          ...memberData.customization,
          photo_url: result.photo_url,
        },
      });

      // Refresh org members
      if (userData?.org_id) {
        await fetchOrgMembers(userData.org_id);
      }

      // Refetch user data if updating own profile
      if (memberData.id === userData?.id) {
        await fetchUserData(userData.id);
      }

      showNotification({
        type: "success",
        message: "Photo de profil mise à jour avec succès",
      });
    } catch (error: any) {
      console.error("Failed to upload profile picture:", error);
      showNotification({
        type: "error",
        message: "Échec du téléchargement de la photo de profil",
      });
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleResendInvitation = async () => {
    if (!memberData) return;

    setIsResendingInvitation(true);
    try {
      await invitationsApi.resend(memberData.id);
      showNotification({
        type: "success",
        message: "L'invitation a été renvoyée avec succès",
      });
    } catch (error: any) {
      console.error("Failed to resend invitation:", error);
      showNotification({
        type: "error",
        message: "Impossible de renvoyer l'invitation. Veuillez réessayer.",
      });
    } finally {
      setIsResendingInvitation(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!memberData) return;

    setIsDeleting(true);
    try {
      await usersApi.delete(memberData.id);

      // Refresh org members
      if (userData?.org_id) {
        await fetchOrgMembers(userData.org_id);
      }

      showNotification({
        type: "success",
        message: "Le compte a été supprimé avec succès",
      });
      onClose();
    } catch (error: any) {
      console.error("Failed to delete user:", error);
      showNotification({
        type: "error",
        message: "Échec de la suppression du compte",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  const canEdit = isOwnerOrAdmin(userData) || userData?.id === memberId;
  return (
    <BaseModal
      title="Membre de l'équipe"
      startIcon={<Icon1 name="user" size={20}></Icon1>}
      onClose={onClose}
    >
      <div className="bg-neutral-100 flex flex-col gap-2 p-4">
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h4 text-neutral-400">Photo de profil</div>
          <div className="relative">
            <UserAvatar
              src={memberData?.customization.photo_url}
              className="w-24 h-24 mb-2 rounded-full border flex-shrink-0"
              value={memberData?.customization.avatar_seed || memberData?.infos.display_name}
              size={96}
            />
            {canEdit && (
              <SecondaryButton
                startIcon={<Icon1 name="join" size={18}></Icon1>}
                label="Changer de photo"
                variant="large"
                onClick={() => setShowPhotoSelector(!showPhotoSelector)}
              ></SecondaryButton>
            )}
          </div>

          {showPhotoSelector && canEdit && (
            <div className="flex flex-col gap-2 mt-2">
              <div className="text-h3 font-semibold text-neutral-600">Choisir un avatar</div>
              <div className="flex flex-row gap-2 flex-wrap">
                {buildSeeds(memberData?.infos.display_name || "user").map((seed) => {
                  const isSelected = !memberData?.customization.photo_url && memberData?.customization.avatar_seed === seed;
                  return (
                    <button
                      key={seed}
                      onClick={() => {
                        if (memberData) {
                          setMemberData({
                            ...memberData,
                            customization: {
                              ...memberData.customization,
                              photo_url: "",
                              avatar_seed: seed,
                            },
                          });
                        }
                      }}
                      className={`rounded-full border-2 transition-all overflow-hidden ${
                        isSelected
                          ? "border-blue-500 ring-2 ring-blue-200"
                          : "border-neutral-200 hover:border-neutral-400"
                      }`}
                    >
                      <Avvvatars value={seed} style="shape" size={32} radius={0} />
                    </button>
                  );
                })}
              </div>
              <div className="text-h3 text-neutral-400">ou</div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleFileUpload}
                className="hidden"
              />
              <SecondaryButton
                startIcon={<Icon1 name="join" size={18} />}
                label={isUploading ? "Téléchargement..." : "Télécharger une photo"}
                variant="large"
                disabled={isUploading}
                onClick={() => fileInputRef.current?.click()}
              />
              {memberData?.customization.photo_url && (
                <SecondaryButton
                  startIcon={<Icon1 name="trash" size={18} />}
                  label="Utiliser l'avatar généré"
                  variant="large"
                  onClick={() => {
                    if (memberData) {
                      setMemberData({
                        ...memberData,
                        customization: {
                          ...memberData.customization,
                          photo_url: "",
                          avatar_seed: memberData.customization.avatar_seed || memberData.infos.display_name,
                        },
                      });
                    }
                  }}
                />
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <TextField
            label="Nom affiché"
            value={memberData?.infos.display_name || ""}
            onChange={(value) => {
              if (memberData) {
                setMemberData({
                  ...memberData,
                  infos: { ...memberData.infos, display_name: value },
                });
              }
            }}
            placeholder="Marie C."
            disabled={!canEdit}
          />
          <TextField
            label="Nom complet"
            value={memberData?.infos.full_name || ""}
            onChange={(value) => {
              if (memberData) {
                setMemberData({
                  ...memberData,
                  infos: { ...memberData.infos, full_name: value },
                });
              }
            }}
            placeholder="Marie Curie"
            disabled={!canEdit}
          />
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <DropDown
                options={Object.entries(role_dict).map(([key, value]) => ({
                  value: key,
                  label: value,
                }))}
                label="Rôle"
                value={memberData?.role || Object.keys(role_dict)[0]}
                onSelect={(value) => {
                  setMemberData({
                    ...(memberData as User),
                    role: String(value),
                  });
                }}
                disabled={!isOwnerOrAdmin(userData)}
              ></DropDown>
            </div>
            <div className="flex-1">
              <DropDown
                options={[
                  { value: "OWNER", label: "Propriétaire" },
                  { value: "ADMIN", label: "Manager" },
                  { value: "MEMBER", label: "Membre" },
                  { value: "GUEST", label: "Invité" },
                ]}
                label="Accès"
                value={memberData?.config.access_type || "MEMBER"}
                onSelect={(value) => {
                  if (memberData) {
                    setMemberData({
                      ...memberData,
                      config: {
                        ...memberData.config,
                        access_type: String(value) as AccessType,
                      },
                    });
                  }
                }}
                disabled={!isOwnerOrAdmin(userData)}
              ></DropDown>
            </div>
          </div>

          <TextField
            label="Adresse e-mail"
            placeholder="marie.curie@life.com"
            value={memberData?.infos.email || ""}
            disabled={true}
          />
          <TextField
            label="Numéro de téléphone"
            placeholder="06 12 34 56 78"
            type="phone"
            value={memberData?.infos.phone_number || ""}
            onChange={(value) => {
              if (memberData) {
                setMemberData({
                  ...memberData,
                  infos: { ...memberData.infos, phone_number: value },
                });
              }
            }}
            disabled={!canEdit}
          />
          <TextField
            label="UID"
            placeholder="uid_1234567890"
            value={memberData?.id}
            disabled={true}
          />
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h4 text-neutral-400">Drapeau personnel</div>
          <MemberItem
            memberName={memberData?.infos.display_name || "Utilisateur"}
            memberColor={memberData?.customization.color_tag || "#000000"}
          ></MemberItem>
          {canEdit && (
            <>
              <SecondaryButton
                startIcon={<Icon1 name="pinceau" size={18}></Icon1>}
                label="Changer de couleur"
                variant="large"
                onClick={() => setShowColorPicker(!showColorPicker)}
              ></SecondaryButton>

              {showColorPicker && (
                <div className="flex flex-col gap-2 mt-2">
                  <div className="text-h5 text-neutral-600 font-semibold">
                    Choisir une couleur
                  </div>
                  <div className="grid grid-cols-6 gap-2">
                    {colorPalette.map((color) => (
                      <button
                        key={color}
                        onClick={() => {
                          if (memberData) {
                            setMemberData({
                              ...memberData,
                              customization: {
                                ...memberData.customization,
                                color_tag: color,
                              },
                            });
                          }
                        }}
                        className={`w-10 h-10 rounded-full transition-all hover:scale-110 ${
                          memberData?.customization.color_tag === color
                            ? "ring-2 ring-offset-2 ring-black"
                            : "hover:ring-2 hover:ring-offset-1 hover:ring-neutral-300"
                        }`}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {isOwnerOrAdmin(userData) && (
          <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
            <SecondaryButton label="Modifier l'adresse email"></SecondaryButton>
            <SecondaryButton label="Réinitialiser le mot de passe"></SecondaryButton>
            {memberData?.status === "pending" && (
              <SecondaryButton
                label={
                  isResendingInvitation
                    ? "Envoi en cours..."
                    : "Renvoyer une invitation"
                }
                disabled={isResendingInvitation}
                onClick={handleResendInvitation}
              />
            )}
            <SecondaryButton
              danger={true}
              label={isDeleting ? "Suppression..." : "Supprimer le compte"}
              disabled={isDeleting}
              onClick={() => setShowDeleteConfirmation(true)}
            ></SecondaryButton>
          </div>
        )}
      </div>
      {saveError && (
        <div className="px-4 text-h4 text-red-500">{saveError}</div>
      )}
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={isSaving ? "Enregistrement..." : "Enregistrer"}
          onClick={handleSave}
          disabled={isSaving || !canEdit}
        ></Button>
      </div>
      {showDeleteConfirmation && (
        <ConfirmationModal
          title={`Supprimer le compte de ${memberData?.infos.display_name || memberData?.infos.email} ?`}
          message="Cette action supprimera définitivement le compte de cet utilisateur. Il ne pourra plus se connecter à la plateforme."
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={handleDeleteAccount}
        />
      )}
    </BaseModal>
  );
};
